/**
 * Dismissible-per-session banner shown to a logged-in but UNVERIFIED user on the riders list and
 * the editor (docs/V2_EMAIL_VERIFICATION.md's client section). Explains what's disabled (writes —
 * saving, publishing, presets), offers a one-click resend, and a link to the profile page's own
 * Security section (where the full email-change flow lives).
 *
 * Self-contained (CLAUDE.md rule #8): a page composes it via `verifyEmailBannerHtml()` in its own
 * template and calls `initVerifyEmailBanner()` once mounted — same two-function shape as
 * `app-header.ts`/`side-nav.ts`.
 *
 * Verification state is NOT read from the locally-stored session (`StoredAuthUser` carries no such
 * field, and login-time state can go stale mid-session) — this fetches `GET /users/me/account`
 * itself on init and shows/hides based on the live answer. Best-effort: a failed fetch just means
 * no banner, never a broken page — the write endpoints are the real enforcement either way.
 */
import { getAccountState, requestEmailVerification } from "../api/account-api";
import { getCurrentUser } from "../auth/auth-service";
import { navigate } from "../app/router";
import { icon } from "./icons";
import { t } from "../i18n";

/** Dismissed for the lifetime of this page load (module-level, survives an in-app page swap since
 *  app-shell never reloads the document — "per session" in the owner's sense: reappears on a fresh
 *  tab/reload, not on every navigate() between riders <-> editor). */
let dismissed = false;

export function verifyEmailBannerHtml(): string {
  return `
    <div id="verify-email-banner" class="hidden" role="status">
      <span id="verify-email-banner-text">${t("verifyBanner.message")}</span>
      <div id="verify-email-banner-actions">
        <button id="verify-email-banner-profile" type="button">${icon("soundman")}${t("verifyBanner.profileLink")}</button>
        <button id="verify-email-banner-resend" type="button">${icon("publish")}${t("verifyBanner.resend")}</button>
        <button id="verify-email-banner-dismiss" type="button" aria-label="${t("verifyBanner.dismissAriaLabel")}">${icon("close")}</button>
      </div>
    </div>`;
}

/** «Resend confirmation email» — одно действие и для кнопки полосы, и для `remindToVerifyEmail`. */
function sendAgain(root: ParentNode, resendLabel: string): void {
  const resendBtn = root.querySelector<HTMLButtonElement>("#verify-email-banner-resend");
  const textEl = root.querySelector<HTMLElement>("#verify-email-banner-text");
  if (!resendBtn || resendBtn.disabled) return;
  const email = getCurrentUser()?.email ?? "";
  resendBtn.disabled = true;
  resendBtn.textContent = t("common.sending");
  void requestEmailVerification(email)
    .then(() => {
      resendBtn.textContent = t("verifyBanner.resendSent");
      setTimeout(() => {
        resendBtn.textContent = resendLabel;
        resendBtn.disabled = false;
      }, 4000);
    })
    .catch(() => {
      resendBtn.textContent = resendLabel;
      resendBtn.disabled = false;
      if (textEl) textEl.textContent = t("verifyBanner.resendFailed");
    });
}

/**
 * «Save» в просьбе сохраниться у того, кто зарегистрировался, но ещё не подтвердил почту
 * (`editor/guest-object-limit.ts`; владелец, 01.11.2026: «пока не подтвердил почту действуют пределы»).
 * Путь тот же, что уже есть: эта полоса — даже если её закрыли — и то же письмо, что по её кнопке.
 * Новых слов нет.
 */
export function remindToVerifyEmail(root: ParentNode): void {
  const banner = root.querySelector<HTMLDivElement>("#verify-email-banner");
  if (!banner) return;
  dismissed = false;
  banner.classList.remove("hidden");
  sendAgain(root, t("verifyBanner.resend"));
}

export function initVerifyEmailBanner(root: ParentNode): void {
  const banner = root.querySelector<HTMLDivElement>("#verify-email-banner");
  // Закрытую полосу всё равно подключаем: `remindToVerifyEmail` может показать её снова, и тогда её
  // кнопки обязаны работать. Показ по-прежнему решает `dismissed` ниже.
  if (!banner || !getCurrentUser()) return;

  const resendBtn = root.querySelector<HTMLButtonElement>("#verify-email-banner-resend");
  const resendLabel = resendBtn?.textContent ?? t("verifyBanner.resend");

  void getAccountState()
    .then((state) => {
      if (dismissed || state.emailVerified) return; // verified since the fetch started, or already dismissed
      banner.classList.remove("hidden");
    })
    .catch(() => undefined);

  resendBtn?.addEventListener("click", () => sendAgain(root, resendLabel));

  root.querySelector<HTMLButtonElement>("#verify-email-banner-profile")?.addEventListener("click", () => {
    navigate({ name: "profile" });
  });

  root.querySelector<HTMLButtonElement>("#verify-email-banner-dismiss")?.addEventListener("click", () => {
    dismissed = true;
    banner.classList.add("hidden");
  });
}
